import { config } from '@/config'
import axios from 'axios'
import type {
  AdpConnectionTestDatabasePayload,
  AdpConnectionTestOptionField,
  AdpConnectionTestResponse,
  AdpDataSource,
  AdpDataSourcesFilterResponse,
  AdpRegisteredSourceDataSource,
  AdpRegisteredSourceDataSourcesResponse,
  AdpConnectionColumnsDatabasePayload,
  AdpConnectionColumnsResponse,
  AdpConnectionColumnItem,
  AdpSourceDataSourceCreatePayload,
  AdpSourceDataSourceCreateResponse,
  AdpTargetDataSourceCreateBody,
  AdpTargetDataSourceCreateResponse,
  AdpPostSourceFilesBody,
  AdpPostSourceFilesResponse,
  AdpSourceFileListItem,
  AdpSourceFilesListResponse,
  AdpSourceFileRegisteredField,
  AdpDqStatsBody,
  AdpDqDataProfilingBody,
  AdpDqStatsBySourceFileResponse,
  AdpDqStatRecord,
  AdpPhysicalTablesBody,
  AdpSimpleMutationResponse,
  AdpDataFlowMappingBody,
  AdpTargetTablesBatchBody,
  AdpTargetTablesBatchResponse,
} from './adpDataSources.types'

/** `column_name` for `/data-sources/filter` — connectors usable as pipeline **sources** (ingest). */
export const ADP_DATA_SOURCES_FILTER_COLUMN_SOURCE =
  'is_supported_for_source_data_source' as const

/** `column_name` for `/data-sources/filter` — connectors usable as pipeline **destinations** (target output). */
export const ADP_DATA_SOURCES_FILTER_COLUMN_TARGET_DESTINATION =
  'is_supported_for_target_data_source' as const

/** Default headers for ADP service `fetch` calls. */
export function getAdpApiHeaders(): HeadersInit {
  return {
    Accept: 'application/json',
    'x-tenant-name': config.adpTenantName,
  }
}

export type FetchAdpDataSourcesFilterParams = {
  column_name: string
  value: string | boolean
  show_schema_definition?: boolean
  show_prerequisite?: boolean
}

/** Strip trailing `/api/v1` so static paths like `assets/img/...` resolve on the same host. */
const CONNECTOR_ICON_FILENAME_MAP: Record<string, string> = {
  'postgresql.svg': 'postgres.svg',
  'mariadb.svg': 'maria-db.svg',
  'mongodb.svg': 'mongo-db.svg',
  'sql-generic.svg': 'mssql.svg',
}

const LOCAL_CONNECTOR_ICON_FILENAMES = new Set<string>([
  'zendesk.svg',
  'servicenow.svg',
  'salesforce.svg',
  'postgres.svg',
  'excel.svg',
  's3.svg',
  'redis.svg',
  'oracle.svg',
  'neo4j.svg',
  'mysql.svg',
  'mssql.svg',
  'mongo-db.svg',
  'maria-db.svg',
  'elasticsearch.svg',
  'druid.svg',
  'cassandra.svg',
  'restapi.svg',
  'timescale.svg',
  'snowflake.svg',
  'saphana.svg',
  'redshift.svg',
])

function localConnectorIconPathFromRaw(imageLink: string): string | null {
  const trimmed = imageLink.trim()
  if (!trimmed) return null
  const filename = trimmed.split('/').pop()?.trim()
  if (!filename) return null
  const normalizedFilename = CONNECTOR_ICON_FILENAME_MAP[filename] ?? filename
  if (!LOCAL_CONNECTOR_ICON_FILENAMES.has(normalizedFilename)) return null
  return `/assets/img/icons/db/${normalizedFilename}`
}

export function resolveAdpStaticAssetUrl(adpApiBase: string, imageLink: string): string {
  const raw = imageLink?.trim()
  if (!raw) return ''
  if (/^https?:\/\//i.test(raw)) return raw
  const localConnectorIconPath = localConnectorIconPathFromRaw(raw)
  if (localConnectorIconPath) return localConnectorIconPath
  const origin = adpApiBase.replace(/\/api\/v1\/?$/i, '').replace(/\/$/, '')
  const path = raw.replace(/^\//, '')
  return `${origin}/${path}`
}

export async function fetchAdpDataSourcesFilter(
  params: FetchAdpDataSourcesFilterParams,
  baseUrl = config.adpApiUrl
): Promise<AdpDataSource[]> {
  const base = baseUrl.replace(/\/$/, '')
  const url = new URL(`${base}/data-sources/filter`)
  url.searchParams.set('column_name', params.column_name)
  url.searchParams.set('value', String(params.value))
  if (params.show_schema_definition !== false) {
    url.searchParams.set('show_schema_definition', 'true')
  }
  if (params.show_prerequisite !== false) {
    url.searchParams.set('show_prerequisite', 'true')
  }

  const res = await fetch(url.toString(), { headers: getAdpApiHeaders() })
  if (!res.ok) {
    throw new Error(`Data sources request failed (${res.status})`)
  }

  const json = (await res.json()) as AdpDataSourcesFilterResponse
  if (!json.success || !Array.isArray(json.data)) {
    throw new Error(json.message || 'Invalid data sources response')
  }
  return json.data
}

export type FetchRegisteredDataSourcesParams = {
  date_input?: string
  allowed_instances?: string
  source_name?: string
  target_name?: string
}

async function fetchRegisteredDataSourcesByPath(
  path: string,
  params: FetchRegisteredDataSourcesParams,
  baseUrl = config.adpApiUrl
): Promise<AdpRegisteredSourceDataSource[]> {
  const base = baseUrl.replace(/\/$/, '')
  const queryParams: Record<string, string> = {}
  if (params.date_input?.trim()) {
    queryParams.date_input = params.date_input.trim()
  }
  if (params.allowed_instances?.trim()) {
    queryParams.allowed_instances = params.allowed_instances.trim()
  }
  if (params.source_name?.trim()) {
    queryParams.source_name = params.source_name.trim()
  }

  const url = `${base}${path}`
  const headers = {
    Accept: 'application/json',
    'x-tenant-name': config.adpTenantName,
  }
  const { data: json } = await axios.get<AdpRegisteredSourceDataSourcesResponse>(url, {
    headers,
    params: queryParams,
  })
  if (!json.success || !Array.isArray(json.data?.sources)) {
    throw new Error(json.message || 'Invalid registered sources response')
  }
  return json.data.sources;
}

export async function fetchRegisteredSourceDataSources(
  params: FetchRegisteredDataSourcesParams,
  baseUrl = config.adpApiUrl
): Promise<AdpRegisteredSourceDataSource[]> {
  return fetchRegisteredDataSourcesByPath('/source-data-sources/registered', params, baseUrl)
}

export async function fetchRegisteredTargetDataSources(
  params: FetchRegisteredDataSourcesParams,
  baseUrl = config.adpApiUrl
): Promise<AdpRegisteredSourceDataSource[]> {
  const base = baseUrl.replace(/\/$/, '')
  const url = new URL(`${base}/target-data-sources/registered`)

  if (params.date_input?.trim()) {
    url.searchParams.set('date_input', params.date_input.trim())
  }
  if (params.allowed_instances?.trim()) {
    url.searchParams.set('allowed_instances', params.allowed_instances.trim())
  }
  if (params.target_name?.trim()) {
    url.searchParams.set('target_name', params.target_name.trim())
  }

  const headers = {
    Accept: 'application/json',
    'x-tenant-name': config.adpTenantName,
  }
  const { data: json } = await axios.get<AdpRegisteredSourceDataSourcesResponse & {
    data?: {
      listOfTargets?: AdpRegisteredSourceDataSource[]
    }
  }>(url.toString(), { headers })

  const targets = json.data?.listOfTargets
  if (!json.success || !Array.isArray(targets)) {
    throw new Error(json.message || 'Invalid registered target sources response')
  }
  return targets;
}

export async function testAdpConnection(
  databases: AdpConnectionTestDatabasePayload[],
  baseUrl = config.adpApiUrl
): Promise<AdpConnectionTestOptionField[]> {
  const base = baseUrl.replace(/\/$/, '')
  const url = `${base}/connections/test`
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      ...getAdpApiHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ databases }),
  })

  const json = (await res.json()) as AdpConnectionTestResponse
  if (!res.ok || !json.success) {
    throw new Error(json.message || `Connection test failed (${res.status})`)
  }

  return json.data?.testConnectionResponse ?? []
}

export async function createAdpSourceDataSource(
  databases: AdpSourceDataSourceCreatePayload[],
  baseUrl = config.adpApiUrl
): Promise<string[]> {
  const base = baseUrl.replace(/\/$/, '')
  const url = `${base}/source-data-sources`
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      ...getAdpApiHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ databases }),
  })

  const json = (await res.json()) as AdpSourceDataSourceCreateResponse
  if (!res.ok || !json.success) {
    throw new Error(json.message || `Source creation failed (${res.status})`)
  }

  return json.data?.[0]?.tables ?? []
}

export async function createAdpTargetDataSource(
  body: AdpTargetDataSourceCreateBody,
  baseUrl = config.adpApiUrl
): Promise<void> {
  const base = baseUrl.replace(/\/$/, '')
  const url = `${base}/target-data-sources`
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      ...getAdpApiHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })

  const json = (await res.json().catch(() => ({}))) as AdpTargetDataSourceCreateResponse
  if (!res.ok || (typeof json.success === 'boolean' && !json.success)) {
    throw new Error(json.message || `Target creation failed (${res.status})`)
  }
}

export async function createAdpPhysicalTables(
  body: AdpPhysicalTablesBody,
  baseUrl = config.adpApiUrl
): Promise<void> {
  const base = baseUrl.replace(/\/$/, '')
  const url = `${base}/physical-tables`
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      ...getAdpApiHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })
  const json = (await res.json().catch(() => ({}))) as AdpSimpleMutationResponse
  if (!res.ok || (typeof json.success === 'boolean' && !json.success)) {
    throw new Error(json.message || `Physical table creation failed (${res.status})`)
  }
}

export async function createAdpTargetTablesBatch(
  body: AdpTargetTablesBatchBody,
  baseUrl = config.adpApiUrl
): Promise<{ target_data_source_id?: number; target_table_id?: number }[]> {
  const base = baseUrl.replace(/\/$/, '')
  // const origin = base.replace(/\/api\/v\d+\/?$/i, '').replace(/\/$/, '')
  const url = `${base}/target-tables/batch`
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      ...getAdpApiHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })
  const json = (await res.json().catch(() => ({}))) as AdpTargetTablesBatchResponse
  if (!res.ok || (typeof json.success === 'boolean' && !json.success)) {
    throw new Error(json.message || `Target tables batch failed (${res.status})`)
  }

  console.log('[Target Tables Batch API] Full response:', json)

  // Handle the specific API response structure with created_table array
  let rows: any[] = []
  if (json.data && typeof json.data === 'object') {
    // Check for created_table array first (the actual API response structure)
    if (Array.isArray((json.data as any).created_table)) {
      rows = (json.data as any).created_table
    }
    // Fallback to direct data array
    else if (Array.isArray(json.data)) {
      rows = json.data
    }
    // Single object
    else {
      rows = [json.data]
    }
  }

  console.log('[Target Tables Batch API] Parsed rows:', rows)

  const result = rows
    .filter((row): row is { target_data_source_id?: number; target_table_id?: number } => !!row && typeof row === 'object')
    .map(row => {
      // Try different possible field name variations
      const targetDataSourceId =
        typeof row.target_data_source_id === 'number' ? row.target_data_source_id :
          typeof (row as any).targetDataSourceId === 'number' ? (row as any).targetDataSourceId :
            typeof (row as any).data_source_id === 'number' ? (row as any).data_source_id :
              undefined

      const targetTableId =
        typeof row.target_table_id === 'number' ? row.target_table_id :
          typeof (row as any).targetTableId === 'number' ? (row as any).targetTableId :
            typeof (row as any).table_id === 'number' ? (row as any).table_id :
              undefined

      return {
        target_data_source_id: targetDataSourceId,
        target_table_id: targetTableId,
      }
    })

  console.log('[Target Tables Batch API] Final result:', result)
  return result
}

export async function fetchAdpConnectionColumns(
  databases: AdpConnectionColumnsDatabasePayload[],
  baseUrl = config.adpApiUrl
): Promise<AdpConnectionColumnItem[]> {
  const base = baseUrl.replace(/\/$/, '')
  const url = `${base}/connections/columns`
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      ...getAdpApiHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ databases }),
  })

  const json = (await res.json()) as AdpConnectionColumnsResponse
  if (!res.ok || !json.success) {
    throw new Error(json.message || `Columns request failed (${res.status})`)
  }
  const rawData = json.data
  if (!Array.isArray(rawData)) return []

  return rawData
    .map((item): AdpConnectionColumnItem | null => {
      if (!item || typeof item !== 'object') return null
      const row = item as Record<string, unknown>
      const columnName =
        String(
          row.column_name ??
            row.columnName ??
            row.field_name ??
            row.fieldName ??
            ''
        ).trim()
      const dataType =
        String(
          row.data_type ??
            row.dataType ??
            row.field_data_type ??
            row.fieldDataType ??
            row.column_data_type ??
            ''
        ).trim()

      if (!columnName) return null
      return {
        column_name: columnName,
        data_type: dataType || 'string',
      }
    })
    .filter((item): item is AdpConnectionColumnItem => item !== null)
}

/** GET `/source-files?source_data_source_id=` — tables + fields already registered for a source. */
export async function fetchAdpSourceFilesList(
  sourceDataSourceId: number,
  baseUrl = config.adpApiUrl
): Promise<AdpSourceFileListItem[]> {
  const base = baseUrl.replace(/\/$/, '')
  const id = String(sourceDataSourceId)
  if (!/^\d+$/.test(id)) {
    throw new Error('source_data_source_id must be a positive integer')
  }
  const url = `${base}/source-files?source_data_source_id=${encodeURIComponent(id)}`
  const res = await fetch(url, { headers: getAdpApiHeaders() })
  const json = (await res.json().catch(() => ({}))) as AdpSourceFilesListResponse & { message?: string }
  if (!res.ok || (typeof json.success === 'boolean' && !json.success)) {
    throw new Error(json.message || `List source files failed (${res.status})`)
  }
  return Array.isArray(json.data) ? json.data : []
}

export async function postAdpSourceFiles(
  body: AdpPostSourceFilesBody,
  baseUrl = config.adpApiUrl
): Promise<AdpSourceFileRegisteredField[]> {
  const base = baseUrl.replace(/\/$/, '')
  const url = `${base}/source-files`
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      ...getAdpApiHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })

  const json = (await res.json().catch(() => ({}))) as AdpPostSourceFilesResponse
  if (!res.ok || (typeof json.success === 'boolean' && !json.success)) {
    throw new Error(json.message || `Save source file failed (${res.status})`)
  }
  return Array.isArray(json.data) ? json.data : []
}

export async function postAdpDqStats(
  body: AdpDqStatsBody,
  baseUrl = config.adpApiUrl
): Promise<void> {
  const base = baseUrl.replace(/\/$/, '')
  const url = `${base}/dq-stats`
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      ...getAdpApiHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })

  const json = (await res.json().catch(() => ({}))) as { success?: boolean; message?: string }
  if (!res.ok || (typeof json.success === 'boolean' && !json.success)) {
    throw new Error(json.message || `DQ stats request failed (${res.status})`)
  }
}

export async function postAdpDqDataProfiling(
  body: AdpDqDataProfilingBody,
  baseUrl = config.adpApiUrl
): Promise<void> {
  const base = baseUrl.replace(/\/$/, '')
  const url = `${base}/dq-stats/data-profiling`
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      ...getAdpApiHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })

  const json = (await res.json().catch(() => ({}))) as { success?: boolean; message?: string }
  if (!res.ok || (typeof json.success === 'boolean' && !json.success)) {
    throw new Error(json.message || `Data profiling request failed (${res.status})`)
  }
}

/** GET `/records/dq_stats` filtered by source_file_id + session_id (profiling metrics per field). */
export async function fetchAdpDqStatsBySourceFileId(
  sourceFileId: number,
  sessionId: string,
  baseUrl = config.adpApiUrl
): Promise<AdpDqStatRecord[]> {
  const base = baseUrl.replace(/\/$/, '')
  const id = String(sourceFileId)
  if (!/^\d+$/.test(id)) {
    throw new Error('source_file_id must be a positive integer for DQ stats fetch')
  }
  const trimmedSessionId = sessionId.trim()
  if (!trimmedSessionId) {
    throw new Error('session_id is required for DQ stats fetch')
  }
  const params = new URLSearchParams({
    source_file_id: id,
    session_id: trimmedSessionId,
  })
  const url = `${base}/records/dq_stats?${params.toString()}`
  const res = await fetch(url, { headers: getAdpApiHeaders() })
  const json = (await res.json().catch(() => ({}))) as AdpDqStatsBySourceFileResponse & {
    message?: string | string[]
  }
  if (!res.ok || (typeof json.success === 'boolean' && !json.success)) {
    const msg = Array.isArray(json.message) ? json.message.join(', ') : json.message
    throw new Error(msg || `DQ stats fetch failed (${res.status})`)
  }
  return Array.isArray(json.data) ? json.data : []
}

export async function postAdpDataFlowMapping(
  dataFlowId: number,
  body: AdpDataFlowMappingBody,
  baseUrl = config.adpApiUrl
): Promise<void> {
  const base = baseUrl.replace(/\/$/, '')
  const id = Number(dataFlowId)
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error('dataFlowId must be a positive integer')
  }
  const url = `${base}/data-flows/${id}/mapping`
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      ...getAdpApiHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })
  const json = (await res.json().catch(() => ({}))) as AdpSimpleMutationResponse
  if (!res.ok || (typeof json.success === 'boolean' && !json.success)) {
    throw new Error(json.message || `Data flow mapping failed (${res.status})`)
  }
}
